import { DepartmentListApiData, OrganizationDepartmentRecordList } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, type OrderingKeyOfUrlKey, URLKeys } from 'noddi-async';
import {
  ApiErrorPageInternal,
  filterUnitFor,
  NoddiAsyncConfirmationDialog,
  NoddiButton,
  NoddiColumnDef,
  NoddiInfoBanner,
  NoddiTable,
  useFilterUnits,
  useNoddiSortOrderParams,
  usePaginationQueryParam
} from 'noddi-ui-web';
import { useState } from 'react';
import { CREATED_AT_FILTER_TEXT } from '../../hooks/filters/Date/useCreatedAtFilter';
import { PrimaryContactLines } from '../UserGroups/PrimaryContactLines';
import { AddDepartmentSheet } from './AddDepartmentSheet';
import { ChangeDepartmentPrimaryContactDialog } from './ChangeDepartmentPrimaryContactDialog';
import { CreateDepartmentSheet } from './CreateDepartmentSheet';
import { invalidateDepartmentQueries } from './invalidateDepartmentQueries';
import { OrganizationCapabilities, OrganizationRoutes } from './types';

const filterUnit = filterUnitFor<NonNullable<DepartmentListApiData['query']>>();

type DepartmentOrderingKey = OrderingKeyOfUrlKey<typeof URLKeys.getPaginatedDepartments>;

const columns: NoddiColumnDef<OrganizationDepartmentRecordList, unknown, DepartmentOrderingKey>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    meta: { orderingKey: 'name' }
  },
  {
    // numCars counts the legacy car.department link. carCount is this
    // department's cars, matching its Cars tab.
    accessorKey: 'carCount',
    header: 'Number of cars',
    meta: { orderingKey: 'num_cars' }
  },
  {
    header: 'Primary contact',
    accessorKey: 'primaryContact',
    cell: ({ row }) => <PrimaryContactLines contact={row.original.primaryContact} />
  },
  {
    accessorKey: 'memberCount',
    header: 'Number of members',
    meta: { orderingKey: 'member_count' }
  }
];

interface Props {
  organizationId: number;
  routes: OrganizationRoutes;
  capabilities: OrganizationCapabilities;
}

export const DepartmentsTab = ({ organizationId, routes, capabilities }: Props) => {
  const { pagination, setPagination } = usePaginationQueryParam();
  const { order, onOrderChange } = useNoddiSortOrderParams({ columns });
  const filters = useFilterUnits([
    filterUnit.locked({ organizationIds: [organizationId] }),
    filterUnit.dateRange('createdAt', { ...CREATED_AT_FILTER_TEXT, exclude: true })
  ]);
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState<OrganizationDepartmentRecordList | null>(null);
  const [departmentToRemove, setDepartmentToRemove] = useState<OrganizationDepartmentRecordList | null>(null);
  const [departmentForContact, setDepartmentForContact] = useState<OrganizationDepartmentRecordList | null>(null);

  const {
    data: departments,
    isPending: isDepartmentsPending,
    isFetching: isDepartmentsFetching,
    error: departmentsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedDepartments,
    input: {
      ...pagination,
      ...filters.apiInput,
      order
    },
    queryConfig: {
      placeholderData: keepPreviousData
    }
  });

  const { mutateAsync: deleteDepartment, isPending: isDeletePending } = noddiAsync.useDelete({
    type: URLKeys.deleteDepartment,
    queryConfig: {
      onSuccess: () => {
        invalidateDepartmentQueries();
        setDepartmentToDelete(null);
      }
    }
  });

  const { mutateAsync: patchDepartment, isPending: isRemovePending } = noddiAsync.usePatch({
    type: URLKeys.patchDepartment,
    queryConfig: {
      onSuccess: () => {
        invalidateDepartmentQueries();
        setDepartmentToRemove(null);
      }
    }
  });

  if (departmentsError) {
    return <ApiErrorPageInternal error={departmentsError} />;
  }

  const tableActions = [
    {
      label: 'Change primary contact',
      actionType: 'edit' as const,
      onClick: (row: OrganizationDepartmentRecordList) => setDepartmentForContact(row)
    },
    {
      label: 'Remove from organization',
      disabled: (row: OrganizationDepartmentRecordList) => row.isLastDepartment,
      actionType: 'remove' as const,
      onClick: (row: OrganizationDepartmentRecordList) => setDepartmentToRemove(row)
    },
    ...(capabilities.canDeleteDepartment
      ? [
          {
            label: 'Delete',
            disabled: (row: OrganizationDepartmentRecordList) => row.isLastDepartment,
            actionType: 'delete' as const,
            onClick: (row: OrganizationDepartmentRecordList) => setDepartmentToDelete(row)
          }
        ]
      : [])
  ];

  return (
    <>
      {departments?.results.some((department) => department.isLastDepartment) && (
        <NoddiInfoBanner
          texts={[
            {
              value:
                'An organization must keep at least one department. Add another department before removing this one.'
            }
          ]}
        />
      )}
      <NoddiTable
        filters={filters}
        columns={columns}
        ordering={order}
        onOrderChange={onOrderChange}
        data={departments?.results ?? []}
        pagination={pagination}
        setPagination={setPagination}
        totalCount={departments?.count}
        isPending={isDepartmentsPending}
        isFetching={isDepartmentsFetching}
        getRowId={(row) => row.id}
        getUrlPath={(row) => ({
          url: routes.department.getPath({ organizationId, departmentId: row.id })
        })}
        actions={tableActions}
        headerNode={
          <div className='flex gap-2'>
            <NoddiButton variant='outline' startIcon='Link' onClick={() => setIsAddSheetOpen(true)}>
              Add existing department
            </NoddiButton>
            <NoddiButton startIcon='Plus' onClick={() => setIsCreateSheetOpen(true)}>
              Create department
            </NoddiButton>
          </div>
        }
      />
      <ChangeDepartmentPrimaryContactDialog
        department={departmentForContact}
        onClose={() => setDepartmentForContact(null)}
      />
      <AddDepartmentSheet open={isAddSheetOpen} onOpenChange={setIsAddSheetOpen} organizationId={organizationId} />
      <CreateDepartmentSheet
        open={isCreateSheetOpen}
        onOpenChange={setIsCreateSheetOpen}
        organizationId={organizationId}
      />
      <NoddiAsyncConfirmationDialog
        open={!!departmentToRemove}
        onOpenChange={(open) => {
          if (!open) {
            setDepartmentToRemove(null);
          }
        }}
        title='Remove department from organization'
        description={`Are you sure you want to remove "${departmentToRemove?.name}" from this organization? The department will not be deleted.`}
        confirmActionLoading={isRemovePending}
        confirmAction={async () => {
          if (departmentToRemove) {
            await patchDepartment({
              departmentId: departmentToRemove.id,
              organizationId: null
            });
          }
        }}
        confirmText='Remove'
      />
      <NoddiAsyncConfirmationDialog
        open={!!departmentToDelete}
        onOpenChange={(open) => {
          if (!open) {
            setDepartmentToDelete(null);
          }
        }}
        title='Delete department'
        description={`Are you sure you want to delete "${departmentToDelete?.name}"? This action cannot be undone.`}
        confirmActionLoading={isDeletePending}
        confirmAction={async () => {
          if (departmentToDelete) {
            await deleteDepartment({ departmentId: departmentToDelete.id });
          }
        }}
        confirmText='Delete'
      />
    </>
  );
};
